import { useMemo, useRef, useState } from 'react';
import type { Survey } from '../types.ts';
import { api } from '../api.ts';
import { QuestionScreen } from './QuestionScreen.tsx';
import { InstructionsScreen } from './InstructionsScreen.tsx';
import { ReviewScreen } from './screens.tsx';

type View = 'instructions' | 'question' | 'review';

export function SurveyRunner({
  slug,
  survey,
  onSubmitted,
}: {
  slug: string;
  survey: Survey;
  onSubmitted: () => void;
}) {
  const { sections, progress } = survey;
  const [answers, setAnswers] = useState<Record<string, number | null>>(survey.answers);
  const [comments, setComments] = useState<Record<string, string>>(survey.comments ?? {});
  const [sectionIdx, setSectionIdx] = useState(0);
  const [questionIdx, setQuestionIdx] = useState(0);
  // Login -> Instructions -> straight into Question 1 for a first-time respondent — no
  // section list, no per-section navigation. Every section's questions run back-to-back in
  // one continuous sequence, ending on the Review screen (shows every answer, lets the
  // respondent jump back to fix any of them, carries the Submit Assessment button). A
  // respondent who already has answers is resuming, not starting — land on Review directly
  // rather than sending them through Instructions again.
  const [view, setView] = useState<View>(progress.answered > 0 ? 'review' : 'instructions');
  const [submitting, setSubmitting] = useState(false);
  // true when a single question was opened from the review screen — answering it
  // returns to review instead of continuing the linear question sequence.
  const [fromReview, setFromReview] = useState(false);
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // ordinal (1-based) of the current question across the whole survey, for the progress bar
  const priorCounts = useMemo(
    () => sections.map((_, i) => sections.slice(0, i).reduce((n, s) => n + s.questions.length, 0)),
    [sections],
  );

  const section = sections[sectionIdx]!;
  const question = section.questions[questionIdx]!;
  const current = priorCounts[sectionIdx]! + questionIdx + 1;

  const advance = () => {
    clearTimeout(advanceTimer.current);
    if (questionIdx < section.questions.length - 1) {
      setQuestionIdx((i) => i + 1);
    } else if (sectionIdx < sections.length - 1) {
      setSectionIdx((i) => i + 1);
      setQuestionIdx(0);
    } else {
      setView('review');
    }
  };

  const returnToReview = () => {
    clearTimeout(advanceTimer.current);
    setFromReview(false);
    setView('review');
  };

  const select = (level: number) => {
    setAnswers((prev) => ({ ...prev, [String(question.externalRef)]: level }));
    const comment = comments[String(question.externalRef)];
    void api.saveAnswer(slug, question.externalRef, level, comment).catch(() => {});
    // No auto-advance — user clicks Next manually to proceed
  };

  const updateComment = (text: string) => {
    setComments((prev) => ({ ...prev, [String(question.externalRef)]: text }));
  };

  // A comment typed with no rating chosen must still be captured — whether the respondent
  // then skips, goes back, or navigates away without ever clicking Next/Skip. Save on blur
  // against whatever answer value (possibly still unanswered/null) currently stands.
  const saveComment = () => {
    const value = answers[String(question.externalRef)] ?? null;
    const comment = comments[String(question.externalRef)];
    void api.saveAnswer(slug, question.externalRef, value, comment).catch(() => {});
  };

  // Skip / clear: persist an explicit null (excluded from all stats) and move on.
  const skip = () => {
    setAnswers((prev) => ({ ...prev, [String(question.externalRef)]: null }));
    const comment = comments[String(question.externalRef)];
    void api.saveAnswer(slug, question.externalRef, null, comment).catch(() => {});
    clearTimeout(advanceTimer.current);
    (fromReview ? returnToReview : advance)();
  };

  const back = () => {
    clearTimeout(advanceTimer.current);
    if (questionIdx > 0) setQuestionIdx((i) => i - 1);
    else if (sectionIdx > 0) {
      const prev = sectionIdx - 1;
      setSectionIdx(prev);
      setQuestionIdx(sections[prev]!.questions.length - 1);
    }
  };

  const editQuestion = (sIdx: number, qIdx: number) => {
    clearTimeout(advanceTimer.current);
    setSectionIdx(sIdx);
    setQuestionIdx(qIdx);
    setFromReview(true);
    setView('question');
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.submit(slug);
      onSubmitted();
    } finally {
      setSubmitting(false);
    }
  };

  if (view === 'instructions') {
    return <InstructionsScreen onContinue={() => setView('question')} />;
  }

  if (view === 'review') {
    const blanks = progress.total - Object.values(answers).filter((v) => typeof v === 'number').length;
    return (
      <ReviewScreen
        sections={sections}
        answers={answers}
        blanks={blanks}
        total={progress.total}
        onEditQuestion={editQuestion}
        onSubmit={submit}
        submitting={submitting}
      />
    );
  }
  return (
    <QuestionScreen
      question={question}
      selected={answers[String(question.externalRef)] ?? undefined}
      comment={comments[String(question.externalRef)] ?? ''}
      onSelect={select}
      onSkip={skip}
      onAdvance={fromReview ? returnToReview : advance}
      onBack={back}
      onCommentChange={updateComment}
      onCommentBlur={saveComment}
      onBackToReview={fromReview ? returnToReview : undefined}
      showBack={!fromReview && (sectionIdx > 0 || questionIdx > 0)}
      progress={{ current, total: progress.total }}
    />
  );
}
